import { createContext, useContext, type ReactNode } from 'react';
import { authClient, type Session } from '@/lib/auth-client';

/**
 * Authentication Context Value
 *
 * Provides session data, loading state, and refetch function throughout the application.
 * Uses Better Auth's useSession hook for automatic session management.
 */
interface AuthContextValue {
  session: Session | null;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Authentication Context
 *
 * Centralized authentication state management using Better Auth.
 * This context wraps Better Auth's useSession hook to provide
 * consistent access to authentication state across the application.
 */
const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * useAuth Hook
 *
 * Access authentication state from any component within the AuthProvider.
 *
 * @throws {Error} If used outside of AuthProvider
 * @returns {AuthContextValue} Current session and loading state
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { session, isLoading } = useAuth();
 *
 *   if (isLoading) return <Loader />;
 *   if (!session) return <LoginPrompt />;
 *
 *   return <div>Welcome, {session.user.email}</div>;
 * }
 * ```
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}

/**
 * AuthProvider Props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider Component
 *
 * Wraps the application (or a section) to provide authentication context.
 * Uses Better Auth's useSession hook to manage session state automatically.
 *
 * @example
 * ```tsx
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { data: session, isPending: isLoading, refetch } = authClient.useSession();

  return (
    <AuthContext.Provider value={{ session, isLoading, refetch }}>{children}</AuthContext.Provider>
  );
}
